/**
 * A page's filter section pinned to the top of the page's scroll area once the page scrolls past it,
 * and folded there to one line, which a toggle unfolds in place. Shared by a report's filter bar
 * (report-filter-bar.js, livewire/report/filter-bar.blade.php) and the Site Analytics page's filter
 * header (site-analytics-filter-header.js), for a page's Alpine scope to spread in.
 *
 *  - Pinned is measured, not assumed from a width: a 1px sentinel before the section leaving the top
 *    of the scroll area (an IntersectionObserver, no scroll handler), less what the section pins
 *    under: the page's own top bar, if it has one (`topBarHeight`, the Insights page's), and its edit
 *    bar while arranging. A section that is not sticky at this width is never pinned.
 *  - Pinned, the section folds to its line, unless something in it is held (focus inside it, or the
 *    page's own `isHeld()`, a popover open), so nothing in it moves focus on its own; let go, it is
 *    whole again. The toggle (aria-expanded) unfolds it in place and folds it back; Escape inside the
 *    unfolded section folds it and returns focus to the toggle.
 *  - Its height while pinned is written to the document element (`height`, 0 while it scrolls with
 *    the page), which the page adds to every element's scroll margin, so nothing focused or scrolled
 *    into view lands under it. On the document element, since each render puts back the page root's
 *    own attributes.
 *  - A spacer after the section holds the page's column at the section's unpinned height in every
 *    state, worked out from state rather than measured after the fact, so the column never changes
 *    height, the browser never shifts the scroll to make up for it, and the sentinel never moves back
 *    into view to unpin the section it just folded.
 *  - Whether the edit bar is pinned too (it can be before the section is) and whether the section is,
 *    are marked on the document element (`editBarPinnedAttribute`, `pinnedAttribute`), where the bar
 *    squares its corners to meet it (components/arrange/edit-bar.blade.php).
 *
 * @param {{
 *   section: string, sentinel: string, toggle: string, editBar: string,
 *   editBarHeight: string, height: string, pinnedAttribute: string, editBarPinnedAttribute: string,
 *   gap: number, topBarHeight?: string, isHeld?: () => boolean, onFold?: () => void,
 * }} options  selectors, the CSS variables and document attributes the page's markup reads, the page
 *             column's gap in px (which the sentinel and the spacer take back), and the page's hooks,
 *             called with the scope as `this`
 */
export function pinnedBar(options) {
    // Kept out of the component's reactive state: an observer or a listener wrapped in Alpine's proxy
    // would be called on the proxy, which the browser refuses.
    let onResize = null;
    let sizer = null;
    let stuckWatcher = null;
    let barWatcher = null;
    let pinnedAbove = null;
    let watchedBar = null;
    let watchedBarTop = null;
    let barStuckWatcher = null;

    // A height the page writes on the document element, in px; 0 when it is not there.
    const measured = (variable) => (variable ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue(variable)) || 0 : 0);

    return {
        /** Whether the section is pinned with the page scrolled under it. */
        pin_stuck: false,

        /** Whether the reader unfolded the pinned section (its toggle). */
        pin_unfolded: false,

        /** Whether focus is inside the section, which keeps it from folding on its own. */
        pin_focusIn: false,

        /** The section's height while it is not pinned, measured; what its pinned states are held to. */
        pin_full: 0,

        /** The pinned section's height folded to its line, and what its line adds when unfolded (measured once seen). */
        pin_slimHeight: 46,
        pin_lineExtra: 44,

        /** Start watching (the page's init()). */
        pin_start() {
            onResize = () => this.pin_measure();
            window.addEventListener('resize', onResize);

            this.pin_measure();

            if (typeof ResizeObserver === 'function' && this.pin_section()) {
                sizer = new ResizeObserver(() => this.pin_measure());
                sizer.observe(this.pin_section());
            }

            this.pin_watchStuck();

            // The top bar's and the edit bar's heights are where the section pins, so the sentinel's
            // line moves with them: watched on the document element, where both bars write theirs.
            if (typeof MutationObserver === 'function') {
                barWatcher = new MutationObserver(() => this.pin_watchStuck());
                barWatcher.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
            }
        },

        /** Stop watching (the page's destroy()). */
        pin_stop() {
            window.removeEventListener('resize', onResize);
            sizer?.disconnect();
            stuckWatcher?.disconnect();
            barWatcher?.disconnect();
            barStuckWatcher?.disconnect();
            document.documentElement.removeAttribute(options.editBarPinnedAttribute);
            document.documentElement.style.removeProperty(options.height);
            document.documentElement.removeAttribute(options.pinnedAttribute);
        },

        pin_section() {
            return this.$root.querySelector(options.section);
        },

        /** Whether the section is pinned (sticky at this width), measured rather than assumed from a width. */
        pin_isPinned() {
            const section = this.pin_section();

            return Boolean(section) && getComputedStyle(section).position === 'sticky';
        },

        /** Whether the section shows only its one line: pinned, scrolled, and not unfolded. */
        pin_slim() {
            return this.pin_stuck && ! this.pin_unfolded;
        },

        /**
         * Watches the sentinel before the section leave the top of the page's scroll area, less the
         * top bar and the edit bar the section pins under (an IntersectionObserver, rebuilt when their
         * heights change).
         */
        pin_watchStuck() {
            this.pin_watchEditBar();

            const height = measured(options.topBarHeight) + measured(options.editBarHeight);

            if (height === pinnedAbove && stuckWatcher) return;

            pinnedAbove = height;
            stuckWatcher?.disconnect();

            const sentinel = this.$root.querySelector(options.sentinel);

            if (! sentinel || typeof IntersectionObserver !== 'function') return;

            let scroller = sentinel.parentElement;

            while (scroller && ! /(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;

            stuckWatcher = new IntersectionObserver((entries) => {
                const entry = entries[entries.length - 1];
                const top = entry.rootBounds ? entry.rootBounds.top : height;

                this.pin_onStuck(this.pin_isPinned() && ! entry.isIntersecting && entry.boundingClientRect.top < top);
            }, { root: scroller ?? null, rootMargin: `-${height}px 0px 0px 0px`, threshold: 0 });
            stuckWatcher.observe(sentinel);
        },

        /**
         * Whether the page's edit bar is pinned to the top of the scroll area (under the page's top
         * bar, if it has one), which it can be before the section is: marked on the document element,
         * where the bar squares its top corners against what is above it. Pinned, its top edge sits on
         * that line, so it no longer fits whole inside an area 1px shorter at the top. Found again as
         * the bar comes and goes with arranging (it writes its height, which the document-element
         * watcher sees), and when the top bar's height changes.
         */
        pin_watchEditBar() {
            const bar = document.querySelector(options.editBar);
            const barTop = measured(options.topBarHeight) + 1;

            if (bar === watchedBar && barTop === watchedBarTop) return;

            barStuckWatcher?.disconnect();
            barStuckWatcher = null;
            watchedBar = bar;
            watchedBarTop = barTop;
            document.documentElement.removeAttribute(options.editBarPinnedAttribute);

            if (! bar || typeof IntersectionObserver !== 'function') return;

            let scroller = bar.parentElement;

            while (scroller && ! /(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;

            barStuckWatcher = new IntersectionObserver((entries) => {
                const entry = entries[entries.length - 1];
                const top = entry.rootBounds ? entry.rootBounds.top : barTop;

                document.documentElement.toggleAttribute(options.editBarPinnedAttribute, entry.intersectionRatio < 1 && entry.boundingClientRect.top <= top);
            }, { root: scroller ?? null, rootMargin: `-${barTop}px 0px 0px 0px`, threshold: [1] });
            barStuckWatcher.observe(bar);
        },

        /**
         * The section was pinned or let go. Pinned, it folds to its line unless something in it is held
         * (focus in it, or the page's isHeld()), when it stays unfolded; let go, it is whole again.
         */
        pin_onStuck(isStuck) {
            if (isStuck === this.pin_stuck) return;

            if (isStuck) {
                this.pin_unfolded = this.pin_focusIn || options.isHeld?.call(this) === true;
            } else {
                this.pin_unfolded = false;
            }

            this.pin_stuck = isStuck;
            document.documentElement.toggleAttribute(options.pinnedAttribute, isStuck);
        },

        /** The toggle: unfolds the pinned section in place, or folds it back. */
        pin_toggleFold() {
            this.pin_unfolded = ! this.pin_unfolded;

            if (! this.pin_unfolded) options.onFold?.call(this);
        },

        /**
         * Escape inside the unfolded pinned section, when nothing in it took the Escape first (a
         * popover or a host's list stops it): the section folds and focus goes back to the toggle.
         */
        pin_foldEscape(event) {
            if (! this.pin_stuck || ! this.pin_unfolded) return;

            event.stopPropagation();
            this.pin_unfolded = false;
            this.$nextTick(() => this.$root.querySelector(options.toggle)?.focus());
        },

        /** The section's height while it is pinned, as `height` on the document element; 0 while it scrolls with the page. */
        pin_measure() {
            const section = this.pin_section();

            if (! section) return;

            const height = section.offsetHeight;

            if (! this.pin_stuck) {
                this.pin_full = height;
            } else if (this.pin_slim()) {
                this.pin_slimHeight = height;
            } else if (this.pin_full > 0) {
                this.pin_lineExtra = height - this.pin_full;
            }

            document.documentElement.style.setProperty(options.height, `${this.pin_isPinned() ? height : 0}px`);
        },

        /**
         * The spacer after the section: folded it adds what the section gave up, unfolded (its line
         * on top) it takes back what the line added, through a negative margin. Its base margin takes
         * back the column's gap.
         */
        pin_spacerStyle() {
            const difference = ! this.pin_stuck || this.pin_full === 0
                ? 0
                : this.pin_full - (this.pin_unfolded ? this.pin_full + this.pin_lineExtra : this.pin_slimHeight);

            return { height: `${Math.max(0, difference)}px`, marginTop: `calc(-${options.gap}px + ${Math.min(0, difference)}px)` };
        },
    };
}
